'use client';

import React, { useMemo } from 'react';
import { CodeWithTooltip } from 'src/components/Strategy/shared';
import {
  extractCodeGroups,
  formatCode,
} from 'src/components/Strategy/shared/utils';
import { getIsProviderFocused } from 'src/utils/getIsProviderFocused';
import { useCodeDataMap } from '../../hooks/useCodeDataMap';
import type { StrategyCodeViewProps } from 'src/components/Strategy/shared/types';

const FRESH_FACES_SUFFIX =
  ' for the first time at a new location in the last 30 days.';

const FRESH_FACES_PREFIXES = {
  providersBilling: 'Show me providers billing ',
  providersReferring: 'Show me providers referring ',
  sitesBilling: 'Show me sites billing ',
} as const;

/**
 * Pick the sentence prefix for a Fresh Faces strategy.
 *
 * "Billing" only reads correctly for treating volume — a referring strategy has
 * to say "referring" (PTRI-211). Site-based strategies can never be referring
 * (the admin toggle forces them back to treating), so there is no sites +
 * referring variant.
 */
const getFreshFacesPrefix = (
  strategyDetails: StrategyCodeViewProps['strategyDetails']
): string => {
  // `null` means we can't tell — fall through to the providers wording.
  if (getIsProviderFocused(strategyDetails) === false) {
    return FRESH_FACES_PREFIXES.sitesBilling;
  }

  return strategyDetails?.volume_type?.toLowerCase() === 'referring'
    ? FRESH_FACES_PREFIXES.providersReferring
    : FRESH_FACES_PREFIXES.providersBilling;
};

/**
 * View for FRESH_FACES strategy type
 * Renders codes with tooltips and template text
 */
const FreshFacesView: React.FC<StrategyCodeViewProps> = ({
  criteriaInputs,
  strategyDetails,
}) => {
  const codeGroups = useMemo(() => {
    return extractCodeGroups(criteriaInputs, strategyDetails);
  }, [criteriaInputs, strategyDetails]);

  const codeDataMap = useCodeDataMap({
    criteriaInputs,
    strategyDetails,
    strategyType: strategyDetails.type,
  });

  const prefix = useMemo(
    () => getFreshFacesPrefix(strategyDetails),
    [strategyDetails]
  );

  if (codeGroups.length === 0) {
    return null;
  }

  // Flatten all codes from all groups for rendering
  const allCodes = codeGroups.flatMap((group) =>
    group.codes.map((code, index) => ({
      ...code,
      groupId: group.componentId,
      originalIndex: index,
    }))
  );

  return (
    <div className="text-secondary text-base leading-relaxed">
      <span data-testid="fresh-faces-prefix">{prefix}</span>
      {allCodes.map((codeItem, index) => {
        const formattedCode = formatCode(codeItem.code, codeItem.type);
        const codeInfo = codeDataMap.get(formattedCode);
        const isLastCode = index === allCodes.length - 1;
        const isSecondToLast = index === allCodes.length - 2;

        return (
          <React.Fragment key={`${codeItem.groupId}-${codeItem.originalIndex}`}>
            <CodeWithTooltip
              code={codeItem.code}
              type={codeItem.type}
              codeData={codeInfo}
            />
            {!isLastCode && <span>{isSecondToLast ? ', or ' : ', '}</span>}
          </React.Fragment>
        );
      })}
      <span>{FRESH_FACES_SUFFIX}</span>
    </div>
  );
};

export default FreshFacesView;
